import React, { useCallback } from "react";

import { Card, CardHeader, CardTitle, DefinitionList } from "../../styles";
import { AutomaticPricingSection } from "./AutomaticPricingSection";
import { countryLabelOf, isPairAssetType, pricingLabelOf } from "./constants";
import { ListingRepairSection } from "./ListingRepairSection";
import { ManualPricingSection } from "./ManualPricingSection";
import { SourceCountrySection } from "./SourceCountrySection";
import {
  InstrumentFixEditorProps,
  ListingRepairDraft,
  ManualPricingValues,
  SourceCountryValues,
} from "./types";

export const InstrumentFixEditor: React.FC<InstrumentFixEditorProps> = ({
  instrument,
  needsPricing,
  label,
  lastPriceLabel,
  isSubmitting,
  errors,
  drafts,
  notice,
  onDraftChange,
  onSaveCountry,
  onRepairListing,
  onUseManualPricing,
  onUseAutomaticPricing,
}) => {
  const { symbol, name, exchange, assetType, nativeCurrency, sourceCountry } =
    instrument;
  const countryLabel = countryLabelOf(sourceCountry);
  const pricingLabel = pricingLabelOf(
    instrument.manualPricingOnly,
    instrument.yahooSymbol,
  );
  const statusLabel = instrument.isActive ? "Active" : "Inactive";
  const isPair = isPairAssetType(assetType);
  const hasLastPrice = lastPriceLabel !== undefined;
  const canRestoreAutomaticPricing =
    isPair && instrument.manualPricingOnly && onUseAutomaticPricing;

  const handleCountryDraft = useCallback(
    (draft: SourceCountryValues | null) => onDraftChange?.("country", draft),
    [onDraftChange],
  );
  const handleListingDraft = useCallback(
    (draft: ListingRepairDraft | null) => onDraftChange?.("listing", draft),
    [onDraftChange],
  );
  const handleManualDraft = useCallback(
    (draft: ManualPricingValues | null) => onDraftChange?.("manual", draft),
    [onDraftChange],
  );

  return (
    <Card aria-label={label ?? `Fix ${symbol}`}>
      <CardHeader>
        <CardTitle>{symbol}</CardTitle>
      </CardHeader>
      <DefinitionList>
        <dt>Name</dt>
        <dd>{name}</dd>
        <dt>Exchange</dt>
        <dd>{exchange}</dd>
        <dt>Asset type</dt>
        <dd>{assetType}</dd>
        <dt>Native currency</dt>
        <dd>{nativeCurrency}</dd>
        <dt>Status</dt>
        <dd>{statusLabel}</dd>
        <dt>Source country</dt>
        <dd>{countryLabel}</dd>
        <dt>Pricing</dt>
        <dd>{pricingLabel}</dd>
        {hasLastPrice && (
          <>
            <dt>Last price</dt>
            <dd>{lastPriceLabel}</dd>
          </>
        )}
      </DefinitionList>
      {notice}
      <SourceCountrySection
        instrument={instrument}
        isSubmitting={isSubmitting}
        error={errors.country}
        draft={drafts?.country}
        onDraftChange={handleCountryDraft}
        onSave={onSaveCountry}
      />
      {needsPricing && (
        <>
          {!isPair && (
            <ListingRepairSection
              instrument={instrument}
              isSubmitting={isSubmitting}
              error={errors.listing}
              draft={drafts?.listing}
              onDraftChange={handleListingDraft}
              onRepair={onRepairListing}
            />
          )}
          {canRestoreAutomaticPricing && (
            <AutomaticPricingSection
              instrument={instrument}
              isSubmitting={isSubmitting}
              error={errors.automatic}
              onConfirm={onUseAutomaticPricing}
            />
          )}
          <ManualPricingSection
            instrument={instrument}
            isSubmitting={isSubmitting}
            error={errors.manual}
            draft={drafts?.manual}
            onDraftChange={handleManualDraft}
            onConfirm={onUseManualPricing}
          />
        </>
      )}
    </Card>
  );
};
